import { usePrivy } from "@privy-io/react-auth"
import { useEffect, useRef, useState } from "react"
import { API_BASE_URL } from "@/lib/constants"
import type { Id } from "~convex/_generated/dataModel"

const CACHE_TTL_MS = 30_000
const MAX_CACHE_ENTRIES = 100

type SearchUsersResult = {
	_id: Id<"users">
	handle: string
	display_name: string
	avatar_url?: string
	bio?: string
	follower_count: number
	isFollowedByMe: boolean | null
	isMirrored?: boolean
}[]
type CacheEntry = {
	fetchedAt: number
	results: SearchUsersResult
}

const resultCache = new Map<string, CacheEntry>()
const inflightRequests = new Map<string, Promise<SearchUsersResult>>()

function buildCacheKey(userId: string | null, query: string) {
	return `${userId ?? "anon"}:${query}`
}

function getCachedResults(cacheKey: string) {
	const cached = resultCache.get(cacheKey)
	if (!cached) {
		return null
	}
	if (Date.now() - cached.fetchedAt > CACHE_TTL_MS) {
		resultCache.delete(cacheKey)
		return null
	}
	return cached.results
}

function setCachedResults(cacheKey: string, results: SearchUsersResult) {
	if (!resultCache.has(cacheKey) && resultCache.size >= MAX_CACHE_ENTRIES) {
		const oldestKey = resultCache.keys().next().value
		if (oldestKey) {
			resultCache.delete(oldestKey)
		}
	}
	resultCache.set(cacheKey, {
		fetchedAt: Date.now(),
		results,
	})
}

const SEARCH_USERS_URL = new URL("/app/search/users", API_BASE_URL).toString()

/**
 * Non-reactive authenticated search over a custom HTTP route. Keeping this off
 * Convex's public function surface prevents unauthenticated callers from
 * hammering the built-in `/api/query` endpoint.
 *
 * Returns `undefined` while loading or when disabled, an array once results land.
 */
export function useSearchUsers(
	query: string,
	enabled: boolean,
	userId: string | null,
): SearchUsersResult | undefined {
	const { getAccessToken } = usePrivy()
	const getAccessTokenRef = useRef(getAccessToken)
	getAccessTokenRef.current = getAccessToken
	const [results, setResults] = useState<SearchUsersResult | undefined>(undefined)

	useEffect(() => {
		const trimmedQuery = query.trim()
		if (!enabled || trimmedQuery.length < 2) {
			setResults(undefined)
			return
		}

		const cacheKey = buildCacheKey(userId, trimmedQuery)
		const cached = getCachedResults(cacheKey)
		if (cached) {
			setResults(cached)
			return
		}

		let cancelled = false

		let request = inflightRequests.get(cacheKey)
		if (!request) {
			request = getAccessTokenRef
				.current()
				.then(async token => {
					if (!token) {
						throw new Error("Missing auth token")
					}

					const response = await fetch(SEARCH_USERS_URL, {
						method: "POST",
						headers: {
							Authorization: `Bearer ${token}`,
							"Content-Type": "application/json",
						},
						body: JSON.stringify({ query: trimmedQuery }),
					})
					if (!response.ok) {
						throw new Error(`Search request failed (${response.status})`)
					}

					return (await response.json()) as SearchUsersResult
				})
				.then(data => {
					setCachedResults(cacheKey, data)
					return data
				})
				.finally(() => {
					if (inflightRequests.get(cacheKey) === request) {
						inflightRequests.delete(cacheKey)
					}
				})
			inflightRequests.set(cacheKey, request)
		}

		request
			.then(data => {
				if (!cancelled) {
					setResults(data)
				}
			})
			.catch(() => {
				if (!cancelled) {
					setResults([])
				}
			})

		return () => {
			cancelled = true
		}
	}, [enabled, query, userId])

	return results
}
